import type {
  Currency,
  DocumentSubmission,
  LockStatusType,
  Month,
  Quarter,
} from './global.types';
import type { Transaction } from './transactions.types';
import type { IUserSnapShot } from './users.types';
import type { IVehicle } from './vehicles.types';

type AccountantReview = {
  reviewStatus: 'reviewed' | 'not_reviewed';
  comments: string;
};

export enum ExpenseCategoryType {
  GOOD = 'good',
  SERVICE = 'service',
}

export enum ReviewStatusEnum {
  reviewed = 'reviewed',
  notReviewd = 'not_reviewed',
}

export enum ECreatedFrom {
  web = 'web',
  dropzone = 'dropzone',
  email = 'email',
  experts = 'experts',
  unknown = 'unknown',
  mobile = 'mobile',
  peppol = 'peppol',
}

export type CreatedFrom = ECreatedFrom;

export type Supplier = {
  name: string;
  // country: SupplierCountryEnum;
  categoryId?: string;
  professionalPart?: number;
};

export type SupplierAsOption = {
  label: string;
  value: string;
  supplier: Supplier;
};

export interface IExpenseCategory {
  id: string;
  title: string;
  displayName: string;
  dnaPercentage: number;
  incomeTaxDeductibility: number;
  maxProfessionalPart?: number | null;
  maxDeductibleVAT?: number;
  reverseChargeVAT?: number;
  vatDeductibleWithoutInvoice?: number;
  recommendedProfessionalPart?: number;
  isCustom?: boolean;
  allowsVAT?: boolean;
  suggestedVATRate?: number | null;
  minAmortizationAmount?: number;
  suggestedAmortizationPeriod?: number;
  [key: string]: any;
}

export enum TripType {
  businessTrip = 'BUSINESS_TRIP',
  driveToWork = 'DRIVE_TO_WORK',
}

export enum TripVehicleType {
  car = 'CAR',
  motorbike = 'MOTORBIKE',
}

export const TripTypeIds = {
  [TripType.businessTrip]: TripType.businessTrip.toLowerCase(),
  [TripType.driveToWork]: TripType.driveToWork.toLowerCase(),
};

export interface VATPeriod {
  year: number;
  quarter?: Quarter;
  month?: Month;
  frequency?: string; //VATReturnFrequencyEnum;
}

export interface ExpenseFile {
  path: string;
  hash?: string | null;
  name?: string;
}

export enum ExpenseGuessedFromTypesEnum {
  aggregatedEmail = 'aggregatedEmail',
  aggregatedIBAN = 'aggregatedIBAN',
  aggregatedOCRMerchantNames = 'aggregatedOCRMerchantNames',
  aggregatedURL = 'aggregatedURL',
  aggregatedVATNumbers = 'aggregatedVATNumbers',
  aggregatedSteuernummers = 'aggregatedSteuernummers',
  userCorrectedMerchantNamesHistory = 'userCorrectedMerchantNamesHistory',
  userEmailHistory = 'userEmailHistory',
  userIBANHistory = 'userIBANHistory',
  userURLHistory = 'userURLHistory',
  userVATNumbersHistory = 'userVATNumbersHistory',
  userSteuernummerHistory = 'userSteuernummerHistory',
  aggregatedValues = 'aggregatedValues',
  VIES = 'VIES',
  OCR = 'OCR',
  NLP = 'NLP',
  KBO = 'KBO',
  BoW = 'BoW',

  // * AL1 only
  AL1_userExpenseOriginalTransaction = 'AL1_userExpenseOriginalTransaction',
  AL1_userIBANHistory = 'AL1_userIBANHistory',
  AL1_aggregatedIBAN = 'AL1_aggregatedIBAN',
  AL1_aggregatedOCRMerchantNames = 'AL1_aggregatedOCRMerchantNames',
  userExpenseOriginalTransaction = 'userExpenseOriginalTransaction',
  userTransactionHistory = 'userTransactionHistory',
  AL1_userTransactionHistory = 'AL1_userTransactionHistory',
  AL1 = 'AL1',

  llm_CategoryOCR = 'llm_CategoryOCR',
}

export enum ExpenseEvidenceReasonEnum {
  extra_eu_supplier_vat_number = 'extra_eu_supplier_vat_number',
  intra_eu_supplier_vat_number = 'intra_eu_supplier_vat_number',
  local_supplier_vat_number = 'local_supplier_vat_number',
  local_supplier_steuernummer = 'local_supplier_steuernummer',
  local_supplier_tax_number = 'local_supplier_tax_number',
  local_supplier_address = 'local_supplier_address',
  extra_eu_supplier_address = 'extra_eu_supplier_address',
  intra_eu_supplier_address = 'intra_eu_supplier_address',
  intra_eu_supplier_tax_number = 'intra_eu_supplier_tax_number',
  local_vat_rate = 'local_vat_rate',
  non_local_vat_rate = 'non_local_vat_rate',
  zero_vat_found = 'zero_vat_found',
  reverse_charge_note = 'reverse_charge_note',
  small_business_exemption_note = 'small_business_exemption_note',
  extra_eu_currency = 'extra_eu_currency',
  intra_eu_currency = 'intra_eu_currency',
  other_item_with_foreign_vat_rate = 'other_item_with_foreign_vat_rate',
  other_item_with_standard_vat_rate = 'other_item_with_standard_vat_rate',
}

type GuessedData = Partial<{
  _id: string;
  llm_response_id: string;
  file_path: string;
  al_version_id: string;
  supplier: Partial<{
    name: string;
    iban: string;
    email: string;
    VAT_number: string;
    steuernummer: string;
    guessed_from: ExpenseGuessedFromTypesEnum;
    url: string;
    address: Partial<{
      city: string;
      zip: string;
      street: string;
      country: string;
    }>;
  }>;
  items: Partial<{
    vat_amount: number;
    net_amount: number;
    category_id: string;
    vat_rate: number;
    professional_part: number;
    guessed_category_from: ExpenseGuessedFromTypesEnum;
    vat_regime: Partial<{
      vat_regime: ExpenseVATRegime;
      evidences: {
        reason: ExpenseEvidenceReasonEnum;
        value: string;
      }[];
    }>;
  }>[];
  currency: string;
  total_amount: number;
  total_vat_amount: number;
  date: string;
  structured_communication: string;
  fraud_detected: boolean;
  is_invoice: boolean;
  original_date: string;
  zero_vat_note: string;
}>;

export enum ExpenseRecurrenceStatus {
  ongoing = 'ongoing',
  paused = 'paused',
  completed = 'completed',
  failed = 'failed',
}

export type ExpenseRecurrence = {
  _id: string;
  next_date: string | null;
  start_date: string;
  end_date?: string;
  template?: {
    file?: ExpenseFile;
  };
  original_expense_date?: string;
  frequency: ExpenseRecurrenceFrequency;
  status: ExpenseRecurrenceStatus;
};

export interface IExpenseV2 {
  _id: string;
  _frontendId?: string;
  userId: string;
  isCreditNote: boolean;
  supplier?: { name?: string };
  notes: string | null;
  // expenseDate: string;
  expenseDateNumber: number;
  // cashRemainder: boolean;
  // matchCard: boolean;
  isInvoice: boolean;
  // VATStatus: ExpenseVATStatus;
  isFake?: boolean;
  // files: ExpenseFile[];
  // fileSource?: string;
  isSuggestedPeriod?: boolean;
  chiftExportIds?: string[];
  // TODO: remove `file*` after migration to `files` key complete
  // filename?: string | null;
  // filePath?: string | null;
  // fileHash?: string | null;

  file?: ExpenseFile | null;
  // createdFrom: CreatedFrom | 'unknown';
  items: IExpenseItemV2[];
  VATAmount: number | null;
  // amountExclVAT: number | null;
  // amount: number;
  // transactions: string[];
  _matchedTransactions: Transaction[]; // frontend value
  payments: { cash: boolean; other_accounts: boolean; transactions: string[] };
  references: { submissions: DocumentSubmission[]; recurrence_id?: string };
  recurrence?: ExpenseRecurrence | null;
  // automatch_transaction_id: string;
  accountantReview?: AccountantReview;
  // accountIndex: number;
  isValidated: boolean;
  // reviewed: boolean;
  // createdBy: 'user' | 'email' | 'bank';
  // userSnapshot: IUserSnapShot;
  origin: { by: string; process: string; from: string };
  user: Pick<IUserSnapShot, 'country' | 'VATType' | 'VATReturnFrequency'>;
  // correctedMerchantName?: string;
  // legalMerchantName?: string;
  // transactionMerchantName?: string;
  period: VATPeriod | null;
  imported: boolean;
  // submissions?: DocumentSubmission[];
  // periodUserAction?: PeriodUserAction;
  // professionalAmount: number;
  recoverableVAT: number;
  // professionalAmountExclRecoverableVAT: number;
  deductibleAmount: number;
  sufficientlyDocumented: boolean;
  // customFeesAmount?: number;
  // originalTransaction: {
  //   counterPartyName?: string;
  //   counterPartyNumber?: string;
  //   communication?: string;
  // };
  created: string;
  modified: string;
  // ocr: OCRExpense & GuessedExpense;
  // gmailMessageId?: string;
  taxPeriod?: VATPeriod | null;
  shouldUpdateTaxStatus?: boolean; // defaults to true on the backend
  // isThereVATOnInvoice?: boolean | null; // frontend value
  taxLock?: LockStatusType;
  currency?: Currency;
  currencyRate?: number;
  currencyAmount?: number;
  baseCurrencyAmount?: number;
  baseCurrency?: Currency;
  guessed_data?: GuessedData;
  create_recurrence?: ExpenseCreateRecurrenceData;
  transactions?: string[];
  filePath?: string;
}

export interface IExpenseItemV2 {
  _id: string;
  // amountExclVAT?: number | null;
  VATAmount?: number | null;
  VATRate?: number | null;
  VATRegime?: ExpenseVATRegime;
  // currencyVATAmount?: number;
  // netPrice?: number | null;
  // amount: number;
  currencyAmount?: number;
  baseCurrencyAmount?: number;
  // qty: number;
  // unitPrice: number;
  // vat?: number | null;
  // categoryId: string;
  // VATStatus: ItemVATStatus;
  professionalPart: number;
  // whyZeroVAT?: ExpenseWhyZeroVAT;
  localVATRate?: number | null;
  name?: string | null;
  isAsset?: boolean;
  amortizationPeriod?: number | null;
  // professionalPartOverride?: number;
  // deductibleVATOverride?: number;
  // dnaPercentageOverride?: number;
  // dnaPercentage?: number;
  category: IExpenseCategory;
  vehicle?: IVehicle;
  // vehicleId?: string;
  // professionalAmount: number;
  // recoverableVAT: number;
  // professionalAmountExclRecoverableVAT: number;
  deductibleAmount: number;
  maxDeductibleVAT: number;
  incomeTaxDeductibility: number;
  isExclVAT?: boolean; // only front end
  // isReverseCharge?: boolean | null; // only front end
  tripType?: TripType;
  tripVehicleType?: TripVehicleType;
  tripTotalKm?: number;
  tripWorkingDays?: number;
  deduction?: number;
}

export type PartialIExpenseV2 = Omit<Partial<IExpenseV2>, 'items'> & {
  items?: Partial<IExpenseItemV2>[];
};

export type IExpenseFormValuesV2 = Omit<
  Partial<IExpenseV2>,
  'items' | 'expenseDateNumber'
> & {
  items: Partial<IExpenseItemV2>[];
  transactionsSuggestedIsActive?: boolean;
  expenseDate?: string;
};

export enum ExpenseFormNames {
  _id = '_id',
  supplierName = 'supplier.name',
  expenseDate = 'expenseDate',
  notes = 'notes',
  items = 'items',
  currency = 'currency',
  currencyRate = 'currencyRate',
  taxPeriod = 'taxPeriod',
  period = 'period',
  userVATType = 'user.VATType',
  transactions = 'payments.transactions',
  cash = 'payments.cash',
  other_accounts = 'payments.other_accounts',
  accountantComments = 'accountantReview.comments',
  accountantStatus = 'accountantReview.reviewStatus',
  isInvoice = 'isInvoice',
  isCreditNote = 'isCreditNote',
  isValidated = 'isValidated',
  filePath = 'file.path',
  transactionsSuggestedIsActive = 'transactionsSuggestedIsActive',
  create_recurrence = 'create_recurrence', // frontend only
  saved_recurrence = 'recurrence',
}

export enum ExpenseFormItemNames {
  category = 'category',
  vehicle = 'vehicle',
  professionalPart = 'professionalPart',
  isAsset = 'isAsset',
  amortizationPeriod = 'amortizationPeriod',
  name = 'name',
  maxDeductibleVAT = 'maxDeductibleVAT',
  incomeTaxDeductibility = 'incomeTaxDeductibility',
  isExclVAT = 'isExclVAT',
  tripType = 'tripType',
  tripVehicleType = 'tripVehicleType',
  tripTotalKm = 'tripTotalKm',
  tripWorkingDays = 'tripWorkingDays',
  deduction = 'deduction',
  currencyAmount = 'currencyAmount',
  VATRate = 'VATRate',
  VATRegime = 'VATRegime',
  localVATRate = 'localVATRate',
}

export enum ExpenseVATRegime {
  // field values
  exempt_item = 'exempt-item',
  eu_reverse_charge = 'eu-reverse-charge',
  extra_eu_reverse_charge = 'extra-eu-supplier',
  franchisee_supplier = 'franchisee-supplier',
  local_reverse_charge = 'local-reverse-charge',
  // other values used in some cases
  standard = 'standard',
  foreign_vat = 'foreign-vat',
}

export enum ExpenseRecurrenceFrequency {
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  yearly = 'yearly',
}

export type ExpenseCreateRecurrenceData = {
  start_date: string;
  end_date?: string;
  frequency: ExpenseRecurrenceFrequency;
  interval?: string | number;
  include_file: boolean;
  next_date: string;
};
